/**
 * Atlas PM · 系统设置 · 任务形态 · 新建 / 编辑弹窗。
 *
 * 字段：code（P6.35 起新建由服务端自动生成，编辑只读）/ name /
 * color / sortOrder / isActive / description（P8.9 备注说明，选填）。
 * 后端错误 message 原样展示，不吞错。
 */

import { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import ColorField from '../../../components/ColorField';
import FormField from '../../../components/FormField';
import Modal from '../../../components/Modal';
import { TASK_CATEGORY_PALETTE } from '../../../components/color-palette';
import { ApiError } from '../../../api/client';
import { type TaskCategoryItem } from './api';
import { useCreateTaskCategory, useUpdateTaskCategory } from './useTaskCategories';

export interface TaskCategoryDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  /** 编辑态必传 */
  category?: TaskCategoryItem | null;
  onClose(): void;
  onSaved?(item: TaskCategoryItem): void;
}

interface FormState {
  code: string;
  name: string;
  color: string;
  description: string;
  sortOrder: string;
  isActive: boolean;
}

const EMPTY: FormState = {
  code: '',
  name: '',
  color: TASK_CATEGORY_PALETTE[0] ?? '',
  description: '',
  sortOrder: '0',
  isActive: true,
};

export default function TaskCategoryDialog({
  open,
  mode,
  category,
  onClose,
  onSaved,
}: TaskCategoryDialogProps) {
  const isEdit = mode === 'edit';
  const createMut = useCreateTaskCategory();
  const updateMut = useUpdateTaskCategory(category?.id ?? '');
  const mutation = isEdit ? updateMut : createMut;
  const busy = mutation.isPending;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // 每次打开时按当前条目回填 / 重置
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setSubmitError(null);
    if (isEdit && category) {
      setForm({
        code: category.code,
        name: category.name,
        color: category.color ?? '',
        description: category.description ?? '',
        sortOrder: String(category.sortOrder),
        isActive: category.isActive,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, isEdit, category]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (key === 'name') setErrors((e) => ({ ...e, name: undefined }));
    setSubmitError(null);
  };

  const onSubmit = () => {
    const nextErrors: { name?: string } = {};
    if (!form.name.trim()) nextErrors.name = '名称不能为空';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const base = {
      name: form.name.trim(),
      color: form.color.trim() || null,
      description: form.description.trim() || null,
      sortOrder: Number(form.sortOrder) || 0,
      isActive: form.isActive,
    };

    const onSuccess = (saved: TaskCategoryItem) => {
      onSaved?.(saved);
      onClose();
    };
    const onError = (err: unknown) =>
      setSubmitError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : '保存失败',
      );

    if (isEdit) {
      updateMut.mutate(base, { onSuccess, onError });
    } else {
      // ⭐ P6.35：不传 code，由服务端自动生成唯一值
      createMut.mutate(base, { onSuccess, onError });
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? '编辑任务形态' : '新建任务形态'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            取消
          </Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={busy}
            data-testid="tc-submit"
          >
            {busy ? '提交中…' : isEdit ? '保存修改' : '创建'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        {submitError && <div className="pm-form__error">⚠ {submitError}</div>}

        <div className="tc-form-grid">
          <FormField
            label="代码"
            htmlFor={isEdit ? 'tc-code' : undefined}
            hint={isEdit ? '代码是身份标识，创建后不可修改' : undefined}
          >
            {isEdit ? (
              <input
                id="tc-code"
                className="pm-input"
                data-testid="tc-field-code"
                value={form.code}
                readOnly
                disabled
              />
            ) : (
              <div className="pm-field__hint" data-testid="tc-code-note">
                代码由系统自动生成
              </div>
            )}
          </FormField>

          <FormField label="名称" required htmlFor="tc-name" error={errors.name}>
            <input
              id="tc-name"
              className="pm-input"
              data-testid="tc-field-name"
              placeholder="如：现场勘察"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
            />
          </FormField>

          <FormField label="排序" htmlFor="tc-sort" hint="数值越小越靠前">
            <input
              id="tc-sort"
              type="number"
              min={0}
              className="pm-input"
              data-testid="tc-field-sort"
              value={form.sortOrder}
              onChange={(e) => setField('sortOrder', e.target.value)}
            />
          </FormField>

          <FormField label="颜色">
            <ColorField
              value={form.color ? form.color : null}
              onChange={(c) => setField('color', c ?? '')}
              palette={TASK_CATEGORY_PALETTE}
              testIdPrefix="tc"
              containerTestId="tc-field-color"
            />
          </FormField>

          <FormField label="状态" htmlFor="tc-active">
            <label className="tc-check">
              <input
                id="tc-active"
                type="checkbox"
                data-testid="tc-field-active"
                checked={form.isActive}
                onChange={(e) => setField('isActive', e.target.checked)}
              />
              启用
            </label>
          </FormField>

          <FormField
            label="备注说明"
            htmlFor="tc-description"
            className="tc-form-col--full"
            hint="选填，最多 200 字；建任务时会在任务形态下方展示"
          >
            <textarea
              id="tc-description"
              className="pm-input"
              data-testid="field-description"
              rows={3}
              maxLength={200}
              placeholder="如：涉及现场实施的常规任务"
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
            />
            <div className="tc-desc-counter">{form.description.length}/200</div>
          </FormField>
        </div>
      </form>
    </Modal>
  );
}
